/**
 * @voltxampere/ui — Scene graph (Sprint 12)
 *
 * Per Blueprint §12.1. The scene is an immutable description the
 * `CanvasRenderer` consumes per frame. Mutation never happens
 * in-place — `sceneFromCircuit` rebuilds a fresh tree whenever the
 * upstream `Circuit` signal or viewport snapshot changes.
 *
 * Sprint 12 implements the `'component'` / `'wire'` / `'grid'`
 * variants. The remaining variants (`'net-label'`, `'annotation'`,
 * `'probe-overlay'`, `'assertion-marker'`, `'selection-box'`,
 * `'ghost'`) compile but are no-op skipped by `drawLayer`. Sprints
 * 13-15 wire them up.
 *
 * `AssertionResult`, `Complex`, and `ComponentDefinition` belong to
 * downstream packages (engine, math, components). To keep the UI
 * package's import graph thin (Kural 4), we type those slots
 * structurally with the minimum the SceneNode union needs. The
 * canvas does not introspect the payloads — it dispatches on
 * `kind` and forwards the runtime instance to the eventual draw
 * helper.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/** Canonical zIndex map, Blueprint §12.1. */
export const LAYER_Z_INDEX = Object.freeze({
    background: 0,
    grid: 1,
    wires: 2,
    components: 3,
    "net-labels": 4,
    annotations: 5,
    probes: 6,
    selection: 7,
    ghost: 8,
});
// ── sceneFromCircuit derivation ─────────────────────────────────────
/**
 * Sprint 12 default symbol body bounds. Symbols ship with a
 * `viewBox = "0 0 40 26"` which our drawComponent translates to
 * world units 1:1; the bounding box for hit testing is the same
 * 40x26 box, recentred at the instance position.
 */
export const DEFAULT_COMPONENT_BOUNDS = Object.freeze({
    x: -20,
    y: -13,
    w: 40,
    h: 26,
});
/**
 * Compute a world-space AABB for a `ComponentInstance` placed at
 * `instance.position`. Sprint 12 ignores `rotation` for bounds
 * (square-ish AABB stays correct for 90° multiples since the
 * viewBox is rectangular but close to symmetric); Sprint 13 will
 * tighten this when wire-snap and drag get more precise.
 */
export function componentBounds(instance) {
    return {
        x: instance.position.x + DEFAULT_COMPONENT_BOUNDS.x,
        y: instance.position.y + DEFAULT_COMPONENT_BOUNDS.y,
        w: DEFAULT_COMPONENT_BOUNDS.w,
        h: DEFAULT_COMPONENT_BOUNDS.h,
    };
}
/**
 * Compute the AABB of a `Wire`'s polyline path. Tiny vertical or
 * horizontal segments grow to a 4 px slab so hit tests can land on
 * a perfectly axis-aligned wire.
 *
 * Sprint 52 ADR-0085 — when `wireCurve === "catenary"` and the wire
 * has exactly two pins, the bbox extends downward by the catenary
 * sag so dirty-region tracking correctly invalidates the curved
 * region (not just the chord). Multi-pin wires keep their polyline
 * bbox even under `"catenary"` because the renderer also falls back
 * to polyline for junctions.
 */
export function wireBounds(wire, wireCurve = "linear") {
    if (wire.path.length === 0) {
        // Degenerate — schema enforces ≥2 points so this is defensive
        // only. Returning a zero-area rect keeps quadtree invariants
        // happy; the wire just won't be selectable.
        return { x: 0, y: 0, w: 0, h: 0 };
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of wire.path) {
        if (p.x < minX)
            minX = p.x;
        if (p.y < minY)
            minY = p.y;
        if (p.x > maxX)
            maxX = p.x;
        if (p.y > maxY)
            maxY = p.y;
    }
    // Sprint 52 — extend downward by catenary sag for two-pin wires.
    let sagExtent = 0;
    if (wireCurve === "catenary" && wire.path.length === 2) {
        const p1 = wire.path[0];
        const p2 = wire.path[1];
        if (p1 !== undefined && p2 !== undefined) {
            const slack = wire.slack ?? 1.05;
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const D = Math.sqrt(dx * dx + dy * dy);
            // Mirror the straight-fallback rules in `catenary-math.ts`.
            if (D >= 20 && Math.abs(dx) >= 5 && slack > 1.001) {
                sagExtent = Math.sqrt((3 * D * D * (slack - 1)) / 4);
            }
        }
    }
    const slab = 4;
    const w = Math.max(maxX - minX, slab);
    const h = Math.max(maxY - minY + sagExtent, slab);
    return {
        x: minX - slab / 2,
        y: minY - slab / 2,
        w: w + slab,
        h: h + slab,
    };
}
/**
 * Build an immutable `Scene` from a `Circuit` snapshot, the
 * current viewport, and the active theme. Called from the
 * `apps/web` `currentScene` derivation effect (`store/canvas.ts`)
 * whenever any of those upstream signals change.
 */
export function sceneFromCircuit(circuit, viewport, theme, opts = {}) {
    const selected = new Set(opts.selectedIds ?? []);
    const hoveredId = opts.hoveredId ?? null;
    const gridSpacing = opts.gridSpacing ?? 16;
    const componentNodes = circuit.components.map((instance) => ({
        kind: "component",
        id: instance.id,
        instance,
        selected: selected.has(instance.id),
        hover: hoveredId === instance.id,
        bounds: componentBounds(instance),
    }));
    const wireNodes = circuit.wires.map((wire) => ({
        kind: "wire",
        id: wire.id,
        wire,
        selected: selected.has(wire.id),
        bounds: wireBounds(wire, opts.wireCurve),
    }));
    const layers = [
        {
            id: "background",
            zIndex: LAYER_Z_INDEX.background,
            visible: true,
            nodes: [],
        },
        {
            id: "grid",
            zIndex: LAYER_Z_INDEX.grid,
            visible: true,
            nodes: [{ kind: "grid", id: "grid", spacing: gridSpacing }],
        },
        {
            id: "wires",
            zIndex: LAYER_Z_INDEX.wires,
            visible: true,
            nodes: wireNodes,
        },
        {
            id: "components",
            zIndex: LAYER_Z_INDEX.components,
            visible: true,
            nodes: componentNodes,
        },
        {
            id: "net-labels",
            zIndex: LAYER_Z_INDEX["net-labels"],
            visible: true,
            nodes: [],
        },
        {
            id: "annotations",
            zIndex: LAYER_Z_INDEX.annotations,
            visible: true,
            nodes: [],
        },
        {
            id: "probes",
            zIndex: LAYER_Z_INDEX.probes,
            visible: true,
            nodes: [],
        },
        {
            id: "selection",
            zIndex: LAYER_Z_INDEX.selection,
            visible: true,
            nodes: [],
        },
        {
            id: "ghost",
            zIndex: LAYER_Z_INDEX.ghost,
            visible: true,
            nodes: [],
        },
    ];
    // `exactOptionalPropertyTypes` blocks `{ wireCurve: undefined }`,
    // so spread the field only when the caller supplied a concrete value.
    const wireCurveOverride = opts.wireCurve;
    return {
        layers,
        viewport,
        theme,
        ...(wireCurveOverride === undefined ? {} : { wireCurve: wireCurveOverride }),
    };
}
//# sourceMappingURL=scene.js.map